import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { Box } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useMixpanel, useNotify } from '../../../../../hooks';
import { useApiClient } from '../../../../../hooks/useRestAPIHooks';
import { maskedModeActions } from '../../../../../redux/modules/maskedMode';
import { SYSTEM_RESOURCE_VISIBILITY } from '../../../../../util/fhir';
import { upsertReviewBaseFingerprintExtension } from '../../../../../util/contentFingerprint';
import { AttachmentArbiterProvider } from '../AttachmentArbiter';
import { ViewMode } from '../Attachments/InlineAttachment';
import { ImpressionEditor } from '../ImpressionEditor';
import { ControlBar } from '../ImpressionEditor/ControlBar';
import { ImpressionItem } from '../ImpressionEditor/ImpressionItem';
import { usePresentationKeyboardShortcuts } from '../ImpressionEditor/useKeyboardShortcuts';
import { useTemplatesContext } from './Context';
import { ReportPresentation } from './ReportPresentation';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.darkGraphite[50],
  },
  body: {
    flex: 1,
    overflow: 'auto',
    position: 'relative',
    height: '100%',
    padding: theme.spacing(2),
    zIndex: 10,
  },
}));

const messages = defineMessages({
  signedOff: {
    id: 'reportEditor.signedOff',
    defaultMessage: 'Signed off',
  },
  signOffFailed: {
    id: 'reportEditor.signOffFailed',
    defaultMessage: 'Failed to sign off. Please try again.',
  },
  selectReport: {
    id: 'reportEditor.selectReport',
    defaultMessage: 'Select a report to edit',
  },
});

export const ReportEditor = ({
  refresh,
  patient,
  onClose,
  onDelete,
  impression,
}: {
  patient: fhir.Patient & { id: string };
  impression: ImpressionItem;
  onCancel?: () => void;
  onClose?: () => void;
  onDelete?: () => void;
  refresh?: () => void;
}) => {
  const classes = useStyles({});
  const apiClient = useApiClient();
  const mixpanel = useMixpanel();
  const { formatMessage } = useIntl();
  const [notify] = useNotify();
  const [isEditing, setIsEditing] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('expanded');
  const [isPresenting, setIsPresenting] = useState(false);
  const { invocations } = useTemplatesContext();
  const isGenerating = useMemo(() => {
    return invocations.some(
      invocation =>
        invocation.status === 'processing' &&
        invocation.agentInput.template_id === impression.templateId,
    );
  }, [invocations, impression.templateId]);

  const handleSignOff = async (signOffType?: 'member' | 'clinic') => {
    if (impression.status === 'completed') {
      return; // already signed off
    }
    try {
      // Patch impression status to completed
      const { data: latestImpression } = await apiClient.request(
        `GET /v1/fhir/dstu3/ClinicalImpression/:id`,
        { id: impression.id },
      );
      const baseSummary =
        typeof latestImpression.summary === 'string'
          ? latestImpression.summary
          : '';
      const extension = await upsertReviewBaseFingerprintExtension(
        latestImpression.extension,
        baseSummary,
      );
      const patched: typeof latestImpression = {
        ...latestImpression,
        status: 'completed',
        extension,
      };
      if (signOffType === 'clinic') {
        patched.meta = patched.meta ?? { tag: [] };
        patched.meta.tag = [
          ...(patched.meta.tag ?? []),
          {
            system: SYSTEM_RESOURCE_VISIBILITY,
            code: 'clinic-only',
          },
        ];
      }

      await apiClient.request(
        `PUT /v1/fhir/dstu3/ClinicalImpression/:resourceId`,
        { resourceId: impression.id, ...patched },
      );
      impression.impression.status = 'completed';
      refresh?.();
      setIsEditing(false);
      notify(formatMessage(messages.signedOff), 'success', 5000);
      mixpanel.track('zori_report_signed_off', {
        impressionType: impression.impressionType,
        impressionId: impression.id,
        visibility: signOffType === 'clinic' ? 'clinic_only' : 'member_visible',
      });
    } catch (err) {
      mixpanel.track('zori_report_sign_off_failed', {
        impressionType: impression.impressionType,
        impressionId: impression.id,
        error: err instanceof Error ? err.message : 'Unknown error',
      });
      notify(formatMessage(messages.signOffFailed), 'error', 5000);
    }
  };

  const handleFileUpload = () => {
    mixpanel.track(`zori_report_file_uploaded`, {
      impressionType: impression.impressionType,
      impressionId: impression.id,
    });
  };

  const handleSave = () =>
    mixpanel.track('zori_report_edited', {
      impressionType: impression.impressionType,
      impressionId: impression.id,
    });

  const handleSaveError = (err: unknown) =>
    mixpanel.track('zori_report_edit_failed', {
      impressionType: impression.impressionType,
      impressionId: impression.id,
      error: err instanceof Error ? err.message : 'Unknown error',
    });

  const reduxDispatch = useDispatch();
  usePresentationKeyboardShortcuts(
    useCallback(
      (newMode: ViewMode) => {
        setViewMode(newMode);
        reduxDispatch(maskedModeActions.setMaskedMode(false));
        setIsPresenting(true);
      },
      [reduxDispatch, setViewMode],
    ),
  );

  return (
    <>
      <AttachmentArbiterProvider
        patient={patient}
        attachments={impression.attachments
          .filter((att): att is { reference: string } => !!att.reference)
          .map(att => att.reference)}
      >
        <ReportPresentation
          isOpen={isPresenting}
          onDismiss={() => {
            setIsPresenting(false);
            setViewMode('expanded');
          }}
          patient={patient}
          impression={impression}
          viewMode={viewMode}
        >
          <Box className={classes.body}>
            {isPresenting && (
              <ImpressionEditor.Component
                viewMode={viewMode}
                patient={patient}
                impression={impression}
                hideTitle
              />
            )}
          </Box>
        </ReportPresentation>
      </AttachmentArbiterProvider>
      <ImpressionEditor.Providers
        patient={patient}
        impression={impression}
        viewMode={viewMode}
        refresh={refresh}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
      >
        <Box className={classes.root}>
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 20,
              backgroundColor: 'background.paper',
            }}
          >
            <ControlBar
              onClose={onClose}
              onSignOff={handleSignOff}
              viewMode={viewMode}
              onSelectViewMode={newMode => {
                setViewMode(newMode);
                if (
                  newMode === 'presentation-expanded' ||
                  newMode === 'presentation-compact'
                ) {
                  setIsPresenting(true);
                }
              }}
              isGenerating={isGenerating}
            />
          </Box>
          <Box className={classes.body}>
            {!isPresenting && (
              <ImpressionEditor.Render
                onDelete={onDelete}
                onFileUpload={handleFileUpload}
                onSave={handleSave}
                onSaveError={handleSaveError}
              />
            )}
          </Box>
        </Box>
      </ImpressionEditor.Providers>
    </>
  );
};
